import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dumbbell,
  Plus,
  Search,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  Video,
  Image,
  Link2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { exercisesAPI } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Modal } from '../components/ui/Modal';
import { ExerciseForm } from '../components/forms/ExerciseForm';
import type { Exercise, ExerciseFormData, GroupedExercises, ExerciseFilters } from '../types';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const equipmentIcons: Record<string, string> = {
  barbell: '🏋️',
  dumbbell: '💪',
  machine: '⚙️',
  cable: '🔗',
  bodyweight: '🤸',
};

export function Exercises() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [muscleGroupFilter, setMuscleGroupFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCustomOnly, setShowCustomOnly] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);
  const [deletingExercise, setDeletingExercise] = useState<Exercise | null>(null);
  const [overridingExercise, setOverridingExercise] = useState<Exercise | null>(null);

  const { data: groupedExercises, isLoading } = useQuery({
    queryKey: ['exercises', 'grouped'],
    queryFn: () => exercisesAPI.getGrouped(),
  });

  const { data: filters } = useQuery({
    queryKey: ['exercises', 'filters'],
    queryFn: () => exercisesAPI.getFilters(),
  });

  const createMutation = useMutation({
    mutationFn: (data: ExerciseFormData) => exercisesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] });
      setIsModalOpen(false);
      toast.success('Ejercicio creado');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ExerciseFormData> }) =>
      exercisesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] });
      setEditingExercise(null);
      toast.success('Ejercicio actualizado');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => exercisesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] });
      setDeletingExercise(null);
      toast.success('Ejercicio eliminado');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const setOverrideMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { videoUrl?: string; imageUrl?: string } }) =>
      exercisesAPI.setOverride(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] });
      setOverridingExercise(null);
      toast.success('Links del ejercicio actualizados');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteOverrideMutation = useMutation({
    mutationFn: (id: string) => exercisesAPI.deleteOverride(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] });
      setOverridingExercise(null);
      toast.success('Links restaurados al catálogo');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleSubmit = (data: ExerciseFormData) => {
    if (editingExercise) {
      updateMutation.mutate({ id: editingExercise.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  // Filter exercises
  const getFilteredExercises = (): GroupedExercises => {
    if (!groupedExercises) return {};

    const filtered: GroupedExercises = {};
    
    Object.entries(groupedExercises as GroupedExercises).forEach(([key, group]) => {
      if (muscleGroupFilter && key !== muscleGroupFilter) return;

      const matchingExercises = group.exercises.filter(ex => {
        const matchesSearch = !search || ex.name.toLowerCase().includes(search.toLowerCase());
        const matchesCategory = !categoryFilter || ex.category === categoryFilter;
        const matchesCustom = !showCustomOnly || ex.isCustom;
        return matchesSearch && matchesCategory && matchesCustom;
      });

      if (matchingExercises.length > 0) {
        filtered[key] = { ...group, exercises: matchingExercises };
      }
    });

    return filtered;
  };

  const filteredExercises = getFilteredExercises();
  const totalExercises = Object.values(filteredExercises).reduce((sum, g) => sum + g.exercises.length, 0);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="section-title flex items-center gap-3">
            <Dumbbell className="w-8 h-8 text-dark-100" />
            Biblioteca de Ejercicios
          </h1>
          <p className="section-subtitle">
            {totalExercises} ejercicios disponibles
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5" />
          Nuevo Ejercicio
        </button>
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants} className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              placeholder="Buscar ejercicio..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          <select
            value={muscleGroupFilter}
            onChange={(e) => setMuscleGroupFilter(e.target.value)}
            className="input w-auto"
          >
            <option value="">Todos los músculos</option>
            {(filters as ExerciseFilters)?.muscleGroups?.map(mg => (
              <option key={mg.value} value={mg.value}>{mg.label}</option>
            ))}
          </select>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="input w-auto"
          >
            <option value="">Todas las categorías</option>
            {(filters as ExerciseFilters)?.categories?.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={showCustomOnly}
              onChange={(e) => setShowCustomOnly(e.target.checked)}
              className="w-4 h-4 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
            />
            <span className="text-dark-300 text-sm">Solo personalizados</span>
          </label>
        </div>
      </motion.div>

      {/* Exercises List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : Object.keys(filteredExercises).length > 0 ? (
        <motion.div variants={itemVariants} className="space-y-4">
          {Object.entries(filteredExercises).map(([key, group]) => (
            <div key={key} className="card overflow-hidden">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(key)}
                className="w-full flex items-center justify-between -m-6 p-6 hover:bg-dark-800/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedGroups[key] ? (
                    <ChevronDown className="w-5 h-5 text-dark-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-dark-400" />
                  )}
                  <h3 className="font-semibold text-dark-100">{group.label}</h3>
                  <span className="badge badge-neutral">{group.exercises.length}</span>
                </div>
              </button>

              {/* Group Content */}
              <AnimatePresence>
                {expandedGroups[key] && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-6 mt-6 border-t border-dark-700/50 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {group.exercises.map((exercise: Exercise) => (
                        <div
                          key={exercise.id}
                          className="flex items-start gap-3 p-3 rounded-xl bg-dark-800/50 group hover:bg-dark-800 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-lg bg-dark-700 flex items-center justify-center text-lg flex-shrink-0">
                            {exercise.equipment ? equipmentIcons[exercise.equipment] || '💪' : '💪'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-dark-100 truncate">{exercise.name}</h4>
                              {exercise.isCustom && (
                                <span className="badge badge-accent text-xs">Personalizado</span>
                              )}
                              {!exercise.isCustom && exercise.overridden && (
                                <span className="badge badge-neutral text-xs inline-flex items-center gap-1">
                                  <Link2 className="w-3 h-3" />
                                  Link propio
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-dark-400 mt-0.5">
                              {exercise.categoryLabel}
                              {exercise.equipment && ` • ${exercise.equipment}`}
                            </p>
                            {exercise.description && (
                              <p className="text-sm text-dark-400 mt-1 line-clamp-1">
                                {exercise.description}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              {exercise.videoUrl && (
                                <a
                                  href={exercise.videoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Video className="w-3 h-3" />
                                  Video
                                </a>
                              )}
                              {exercise.imageUrl && (
                                <a
                                  href={exercise.imageUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Image className="w-3 h-3" />
                                  Imagen
                                </a>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() =>
                                exercise.isCustom
                                  ? setEditingExercise(exercise)
                                  : setOverridingExercise(exercise)
                              }
                              title={exercise.isCustom ? 'Editar ejercicio' : 'Editar links (video/imagen)'}
                              className="p-1.5 rounded-lg text-dark-400 hover:text-dark-100 hover:bg-dark-700 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {exercise.isCustom && (
                              <button
                                onClick={() => setDeletingExercise(exercise)}
                                className="p-1.5 rounded-lg text-dark-400 hover:text-red-400 hover:bg-dark-700 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </motion.div>
      ) : (
        <motion.div variants={itemVariants} className="card text-center py-12">
          <Dumbbell className="w-16 h-16 mx-auto text-dark-600 mb-4" />
          <h3 className="text-lg font-semibold text-dark-300 mb-2">
            {search || muscleGroupFilter || categoryFilter || showCustomOnly
              ? 'No se encontraron ejercicios'
              : 'Sin ejercicios'}
          </h3>
          <p className="text-dark-400 mb-6">
            {search || muscleGroupFilter || categoryFilter || showCustomOnly
              ? 'Intenta con otros filtros'
              : 'La biblioteca de ejercicios está vacía'}
          </p>
          {!search && !muscleGroupFilter && !categoryFilter && !showCustomOnly && (
            <button onClick={() => setIsModalOpen(true)} className="btn-primary">
              <Plus className="w-5 h-5" />
              Agregar Ejercicio
            </button>
          )}
        </motion.div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen || !!editingExercise}
        onClose={() => {
          setIsModalOpen(false);
          setEditingExercise(null);
        }}
        title={editingExercise ? 'Editar Ejercicio' : 'Nuevo Ejercicio'}
        size="lg"
      >
        <ExerciseForm
          initialData={editingExercise || undefined}
          filters={filters as ExerciseFilters}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingExercise(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingExercise}
        onClose={() => setDeletingExercise(null)}
        title="Eliminar Ejercicio"
      >
        <div className="space-y-4">
          <p className="text-dark-300">
            ¿Estás seguro de que deseas eliminar{' '}
            <span className="font-semibold text-dark-100">{deletingExercise?.name}</span>?
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeletingExercise(null)} className="btn-secondary">
              Cancelar
            </button>
            <button
              onClick={() => deletingExercise && deleteMutation.mutate(deletingExercise.id)}
              disabled={deleteMutation.isPending}
              className="btn-danger"
            >
              {deleteMutation.isPending ? <LoadingSpinner size="sm" /> : (
                <><Trash2 className="w-4 h-4" />Eliminar</>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Override de links en ejercicio global */}
      <Modal
        isOpen={!!overridingExercise}
        onClose={() => setOverridingExercise(null)}
        title="Editar links del ejercicio"
      >
        {overridingExercise && (
          <LinkOverrideForm
            key={overridingExercise.id}
            exercise={overridingExercise}
            onCancel={() => setOverridingExercise(null)}
            onSave={(data) => setOverrideMutation.mutate({ id: overridingExercise.id, data })}
            onRemove={() => deleteOverrideMutation.mutate(overridingExercise.id)}
            isSaving={setOverrideMutation.isPending}
            isRemoving={deleteOverrideMutation.isPending}
          />
        )}
      </Modal>
    </motion.div>
  );
}

/**
 * Form para pisar video/imagen de un ejercicio del catálogo global. No duplica
 * el ejercicio: crea un override propio del entrenador (visible para sus alumnos).
 */
function LinkOverrideForm({
  exercise,
  onCancel,
  onSave,
  onRemove,
  isSaving,
  isRemoving,
}: {
  exercise: Exercise;
  onCancel: () => void;
  onSave: (data: { videoUrl?: string; imageUrl?: string }) => void;
  onRemove: () => void;
  isSaving: boolean;
  isRemoving: boolean;
}) {
  const [videoUrl, setVideoUrl] = useState(exercise.videoUrl ?? '');
  const [imageUrl, setImageUrl] = useState(exercise.imageUrl ?? '');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSave({ videoUrl: videoUrl.trim(), imageUrl: imageUrl.trim() });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-dark-400">
        Poné tus propios links para{' '}
        <span className="font-semibold text-dark-100">{exercise.name}</span>. Solo los ven vos y tus
        alumnos; no modifica el catálogo global.
      </p>

      <div>
        <label className="block text-sm text-dark-300 mb-1">URL del video</label>
        <div className="relative">
          <Video className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://youtube.com/..."
            className="input pl-9"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-dark-300 mb-1">URL de la imagen</label>
        <div className="relative">
          <Image className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
          <input
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://..."
            className="input pl-9"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {exercise.overridden ? (
          <button
            type="button"
            onClick={onRemove}
            disabled={isRemoving || isSaving}
            className="btn-secondary text-red-400 hover:text-red-300"
          >
            {isRemoving ? <LoadingSpinner size="sm" /> : 'Restaurar catálogo'}
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-3">
          <button type="button" onClick={onCancel} className="btn-secondary">
            Cancelar
          </button>
          <button type="submit" disabled={isSaving || isRemoving} className="btn-primary">
            {isSaving ? <LoadingSpinner size="sm" /> : 'Guardar'}
          </button>
        </div>
      </div>
    </form>
  );
}

